import React, { useMemo } from 'react';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import {
  InfoCard,
  Progress,
  ResponseErrorPanel,
  EmptyState,
} from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import useAsync from 'react-use/lib/useAsync';
import Grid2 from '@mui/material/Grid2';
import { Typography } from '@mui/material';

// Some components' New Relic entities don't follow the catalog name closely
// enough for fuzzy matching (e.g. "speedyfulfil-api" -> "SF-FULFILL-API-PROD"),
// so this lets a component point at the exact New Relic entity name instead.
const APM_ENTITY_NAME_ANNOTATION = 'newrelic.com/apm-entity-name';

type NormalizedEntitySummary = {
  guid: string;
  name: string;
  errorRate: number;
  responseTimeAverage: number;
  throughput: number;
  alertSeverity?: string;
};

type RawEntitySummary = {
  guid: string;
  name: string;
  alertSeverity?: string;
  apmSummary?: {
    errorRate: number;
    responseTimeAverage: number;
    throughput: number;
  };
  browserSummary?: {
    jsErrorRate: number;
    pageLoadTimeAverage: number;
    pageLoadThroughput: number;
  };
};

type ComponentMatch = NormalizedEntitySummary & {
  componentName: string;
  type?: string;
};

// New Relic entitySearch query strings don't support parameterization, so
// component/annotation values are stripped of quotes/backslashes before being
// embedded.
const sanitizeForQuery = (value: string) => value.replace(/["\\]/g, '');

export const normalize = (raw: RawEntitySummary): NormalizedEntitySummary | undefined => {
  if (raw.apmSummary) {
    return {
      guid: raw.guid,
      name: raw.name,
      alertSeverity: raw.alertSeverity,
      errorRate: raw.apmSummary.errorRate,
      responseTimeAverage: raw.apmSummary.responseTimeAverage,
      throughput: raw.apmSummary.throughput,
    };
  }
  if (raw.browserSummary) {
    return {
      guid: raw.guid,
      name: raw.name,
      alertSeverity: raw.alertSeverity,
      errorRate: raw.browserSummary.jsErrorRate,
      responseTimeAverage: raw.browserSummary.pageLoadTimeAverage,
      throughput: raw.browserSummary.pageLoadThroughput,
    };
  }
  return undefined;
};

export const weightedAverage = (
  list: ComponentMatch[],
  pick: (m: ComponentMatch) => number,
) => {
  const throughput = list.reduce((sum, m) => sum + m.throughput, 0);
  return throughput > 0
    ? list.reduce((sum, m) => sum + pick(m) * m.throughput, 0) / throughput
    : list.reduce((sum, m) => sum + pick(m), 0) / list.length;
};

const Stat = ({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: string;
}) => (
  <Grid2 size={3}>
    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
      {label}
    </Typography>
    <Typography variant="h5" sx={{ fontWeight: 700, color }}>
      {value}
    </Typography>
  </Grid2>
);

const SystemHealthCard = () => {
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const catalogApi = useApi(catalogApiRef);

  const componentNames = useMemo(
    () =>
      Array.from(
        new Set(
          (entity.relations ?? [])
            .filter(r => r.type === 'hasPart' && r.targetRef.startsWith('component:'))
            .map(r => r.targetRef.split('/').pop())
            .filter((name): name is string => Boolean(name)),
        ),
      ),
    [entity],
  );

  const { value, loading, error } = useAsync(async () => {
    if (componentNames.length === 0) {
      return { matches: [] as ComponentMatch[], totalComponents: 0 };
    }

    const { items: componentEntities } = await catalogApi.getEntities({
      filter: { kind: 'Component', 'metadata.name': componentNames },
    });
    const typeByComponent = new Map<string, string | undefined>(
      componentEntities.map(e => [e.metadata.name, e.spec?.type as string | undefined]),
    );
    // Prefer an explicit override annotation for the search term; fall back
    // to the catalog component name for everything else.
    const searchTermByComponent = new Map<string, string>(
      componentNames.map(name => {
        const override = componentEntities.find(e => e.metadata.name === name)
          ?.metadata.annotations?.[APM_ENTITY_NAME_ANNOTATION];
        return [name, override ?? name];
      }),
    );

    const proxyUrl = await discoveryApi.getBaseUrl('proxy');
    const graphqlUrl = `${proxyUrl}/newrelic/api/graphql`;
    const graphql = async (query: string) => {
      const response = await fetchApi.fetch(graphqlUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query }),
      });
      if (!response.ok) {
        throw new Error(
          `New Relic query failed: ${response.status} ${response.statusText}`,
        );
      }
      return response.json();
    };

    const searchTerms = Array.from(new Set(searchTermByComponent.values()));
    const nameClauses = searchTerms
      .map(term => `name LIKE '%${sanitizeForQuery(term)}%'`)
      .join(' OR ');
    const searchQuery = `(domain = 'APM' OR domain = 'BROWSER') AND (${nameClauses})`.replace(
      /"/g,
      '\\"',
    );

    const searchBody = await graphql(
      `{ actor { entitySearch(query: "${searchQuery}") { results { entities { guid name } } } } }`,
    );
    const candidates: { guid: string; name: string }[] =
      searchBody.data?.actor?.entitySearch?.results?.entities ?? [];

    // Best-effort name match per component - prefer a "prod" entity when a
    // component has apps in multiple environments.
    const guidByComponent = new Map<string, { guid: string; name: string }>();
    for (const [componentName, searchTerm] of searchTermByComponent.entries()) {
      const lower = searchTerm.toLowerCase();
      const found = candidates.filter(c => c.name.toLowerCase().includes(lower));
      if (found.length === 0) continue;
      const prod = found.find(c => c.name.toLowerCase().includes('prod'));
      guidByComponent.set(componentName, prod ?? found[0]);
    }

    const guids = Array.from(
      new Set(Array.from(guidByComponent.values()).map(c => c.guid)),
    );
    if (guids.length === 0) {
      return { matches: [], totalComponents: componentNames.length };
    }

    // NerdGraph's entities(guids: [...]) rejects more than 25 guids per call.
    const GUID_BATCH_SIZE = 25;
    const guidBatches: string[][] = [];
    for (let i = 0; i < guids.length; i += GUID_BATCH_SIZE) {
      guidBatches.push(guids.slice(i, i + GUID_BATCH_SIZE));
    }

    const detailsBodies = await Promise.all(
      guidBatches.map(batch => {
        const guidList = batch.map(g => `"${g}"`).join(', ');
        return graphql(
          `{ actor { entities(guids: [${guidList}]) { guid name alertSeverity ... on ApmApplicationEntity { apmSummary { errorRate responseTimeAverage throughput } } ... on BrowserApplicationEntity { browserSummary { jsErrorRate pageLoadTimeAverage pageLoadThroughput } } } } }`,
        );
      }),
    );
    const detailsByGuid = new Map<string, RawEntitySummary>(
      detailsBodies
        .flatMap(body => body.data?.actor?.entities ?? [])
        .map((e: RawEntitySummary) => [e.guid, e]),
    );

    const matches: ComponentMatch[] = [];
    for (const [componentName, candidate] of guidByComponent.entries()) {
      const details = detailsByGuid.get(candidate.guid);
      const normalized = details && normalize(details);
      if (!normalized) continue;
      matches.push({
        ...normalized,
        componentName,
        type: typeByComponent.get(componentName),
      });
    }

    return { matches, totalComponents: componentNames.length };
  }, [componentNames.join(',')]);

  if (loading) {
    return (
      <InfoCard title="System Health">
        <Progress />
      </InfoCard>
    );
  }

  if (error) {
    return (
      <InfoCard title="System Health">
        <ResponseErrorPanel error={error} />
      </InfoCard>
    );
  }

  const matches = value?.matches ?? [];
  const totalComponents = value?.totalComponents ?? 0;

  if (matches.length === 0) {
    return (
      <InfoCard title="System Health">
        <EmptyState
          missing="data"
          title="No monitored components found"
          description="None of this system's components could be matched to a New Relic APM or Browser application by name."
        />
      </InfoCard>
    );
  }

  const totalThroughput = matches.reduce((sum, m) => sum + m.throughput, 0);
  const avgErrorRate = weightedAverage(matches, m => m.errorRate);
  // Response time only reflects user-facing latency (frontends/API) - the many
  // background/scheduled lambdas skew it upward with long batch-job durations
  // that aren't a useful "is the system slow" signal.
  const responseTimeMatches = matches.filter(m => m.type !== 'lambda');
  const avgResponseTime =
    responseTimeMatches.length > 0
      ? weightedAverage(responseTimeMatches, m => m.responseTimeAverage)
      : undefined;
  const activeAlerts = matches.filter(
    m => m.alertSeverity === 'CRITICAL' || m.alertSeverity === 'WARNING',
  ).length;

  return (
    <InfoCard title="System Health" variant="gridItem">
      <Grid2 container spacing={2}>
        <Stat
          label="Error Rate"
          value={`${(avgErrorRate * 100).toFixed(2)}%`}
          color={avgErrorRate > 0.01 ? 'error.main' : undefined}
        />
        <Stat
          label="Avg Response Time"
          value={
            avgResponseTime !== undefined
              ? `${(avgResponseTime * 1000).toFixed(0)} ms`
              : 'N/A'
          }
        />
        <Stat label="Throughput" value={`${totalThroughput.toFixed(1)} rpm`} />
        <Stat
          label="Active Alerts"
          value={String(activeAlerts)}
          color={activeAlerts > 0 ? 'error.main' : 'success.main'}
        />
      </Grid2>
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', marginTop: 2, display: 'block' }}
      >
        {matches.length} of {totalComponents} components monitored via New Relic
      </Typography>
    </InfoCard>
  );
};

export default SystemHealthCard;
